// Listado de pre-lanzamientos — ADR-2026-09-06-lanzamientos-modelo-de-datos.
//
// Dos grupos: los que se están preparando y los ya publicados. La acción de la
// pantalla es entrar a uno; crear es el atajo de arriba. Desde
// ADR-2026-10-05-la-lista-de-la-tienda-se-explica-sola la pantalla cuenta cómo
// funciona (tres pasos) y cada card muestra sus fotos, su tienda y su avance.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Loader2,
  ArrowRight,
  Calendar,
  AlertCircle,
  ImagePlus,
  ClipboardList,
  Store,
  Images,
  Rocket,
  X,
  Check,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { funcionAvanzadaActiva, useFuncionesAvanzadas } from '../../hooks/useFuncionesAvanzadas';
import {
  crearLanzamiento,
  fetchLanzamientos,
  type CanalLanzamiento,
  type EstadoLanzamiento,
  type Lanzamiento,
} from './lanzamientosApi';
// Los destinos y sus reglas viven en un solo lugar: este selector, el de
// "Enviar a lanzamiento" y la validación de publicar leen la misma lista.
import { canalesParaElegir, canalesPorGrupo, creaProductos, nombresDeDestinos } from './canales';
import { useMercadoLibreDisponible } from '../../hooks/useMercadoLibreDisponible';
import IconoDeCanal from './IconoDeCanal';
import CachedImg from '../../components/CachedImg';
import { boton } from './botones';
import { useMetricasVisibles } from './metricas/comunes';
import { enEnsayo } from '../../lib/ensayoPrimeraVez';
import { ResumenDeMetricas } from './metricas/ResumenDeMetricas';
import {
  cerrarRecorridoEnCurso,
  pasosDelDestino,
  pasosDelInicio,
  useRecorrido,
} from './recorridos';

const ESTADOS: Record<EstadoLanzamiento, { texto: string; clase: string; punto: string }> = {
  borrador: { texto: 'En preparación', clase: 'bg-white/95 text-amber-800', punto: 'bg-amber-400' },
  publicando: { texto: 'Publicando', clase: 'bg-white/95 text-sky-700', punto: 'bg-sky-500' },
  publicado: { texto: 'Publicado', clase: 'bg-white/95 text-emerald-700', punto: 'bg-emerald-500' },
  con_errores: { texto: 'Con errores', clase: 'bg-white/95 text-red-700', punto: 'bg-red-500' },
};

export default function Lanzamientos() {
  const navigate = useNavigate();
  const { profile, currentBrandId } = useBrandProfile();
  const funcionesAvanzadas = useFuncionesAvanzadas();
  const habilitado = funcionAvanzadaActiva(funcionesAvanzadas.funciones, 'pre_lanzamientos', funcionesAvanzadas.plan);
  /**
   * Mientras no se sabe si está prendida, ni el cartel de "desactivados" ni la lista:
   * en una sesión nueva el cartel aparecía un segundo aunque la función estuviera
   * prendida (ADR-2026-10-05-la-lista-de-la-tienda-se-explica-sola).
   */
  const decidiendo = !habilitado && (funcionesAvanzadas.cargando || funcionesAvanzadas.planCargando);
  const [lanzamientos, setLanzamientos] = useState<Lanzamiento[]>([]);
  const [cargando, setCargando] = useState(true);
  const [creando, setCreando] = useState(false);
  // `?nuevo=1`: se llegó con la intención de crear uno (el botón "Publicar en tu
  // tienda" de Creaciones), así que el modal arranca abierto. Se lee UNA vez al
  // montar y el parámetro se limpia: recargar o volver atrás no lo reabre.
  const [params, setParams] = useSearchParams();
  const [abrirNuevo] = useState(() => params.get('nuevo') === '1');
  useEffect(() => {
    if (!params.has('nuevo')) return;
    const sin = new URLSearchParams(params);
    sin.delete('nuevo');
    setParams(sin, { replace: true });
  }, [params, setParams]);

  const companyId = profile?.id_company ?? null;
  /**
   * Ensayo de primera vez (sólo desarrollo, `?primeraVez=1`): la sección se ve como la
   * ve alguien que nunca entró — sin lanzamientos y sin tiendas conectadas
   * (ADR-2026-10-06-ensayo-de-primera-vez).
   */
  const [ensayo] = useState(enEnsayo);
  /** Métricas sólo para empresas dev_tester (ADR-2026-10-07-metricas-solo-para-dev-testers). */
  const metricasVisibles = useMetricasVisibles();

  const cargar = useCallback(async () => {
    if (!companyId) return;
    if (ensayo) {
      setLanzamientos([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    setLanzamientos(await fetchLanzamientos(companyId, currentBrandId, { conPortadas: true }));
    setCargando(false);
  }, [companyId, currentBrandId, ensayo]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const { preparando, publicados } = useMemo(
    () => ({
      preparando: lanzamientos.filter((l) => l.estado !== 'publicado'),
      publicados: lanzamientos.filter((l) => l.estado === 'publicado'),
    }),
    [lanzamientos],
  );

  // El primer cartel del módulo: con la lista vacía, señala "Nuevo lanzamiento"
  // (ADR-2026-10-01-recorridos-de-tienda-online). Antes del `return` temprano: un
  // hook no puede quedar detrás de un `if`. Espera solo a que se cierre el anuncio
  // de la sección y el modal de crear, si se llegó con `?nuevo=1`.
  useRecorrido(
    'tienda_tour_inicio',
    habilitado && !cargando && !!companyId && lanzamientos.length === 0,
    pasosDelInicio,
  );

  if (decidiendo) {
    // El mismo armado que la página cargando: título y cards esqueleto en su lugar.
    return (
      <div className="mx-auto max-w-6xl py-6 px-4 md:py-10 md:px-8">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold text-brand-black md:text-2xl">Tienda online</h1>
        </div>
        <EsqueletoDeLanzamientos />
      </div>
    );
  }

  if (!habilitado) {
    return (
      <div className="mx-auto max-w-2xl py-16 px-6 text-center">
        <AlertCircle className="mx-auto w-8 h-8 text-neutral-300" />
        <h1 className="mt-4 text-lg font-semibold text-brand-black">
          Los pre-lanzamientos están desactivados
        </h1>
        <p className="mt-2 text-sm text-neutral-500">
          Se activan desde Configuración, en Funciones avanzadas.
        </p>
        <button
          type="button"
          onClick={() => navigate('/configuracion', { state: { section: 'funcionesAvanzadas' } })}
          className={boton('primario', { extra: 'mt-5' })}
        >
          Ir a Configuración
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl py-6 px-4 md:py-10 md:px-8">
      {/* El botón va en el renglón del título, no al costado del bloque entero: con la
          bajada abajo, alinearlo al bloque lo dejaba flotando más alto que el título. */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-brand-black md:text-2xl">Tienda online</h1>
        <NuevoLanzamiento
          companyId={companyId}
          brandId={currentBrandId}
          creando={creando}
          setCreando={setCreando}
          abiertoAlInicio={abrirNuevo}
          onCreado={(l) => navigate(`/lanzamientos/${l.id}`)}
        />
      </div>
      <p className="mt-1 max-w-2xl text-sm text-neutral-500">
        {metricasVisibles
          ? 'Lo que estás preparando para publicar y cómo vienen tus ventas, en un solo lugar.'
          : 'Armá un lanzamiento con las fotos que creaste en Atlas, completá la ficha de cada producto y publicalos todos juntos en tu tienda.'}
      </p>

      {/* Lanzamientos y métricas en la MISMA página, sin pestañas: primero lo que se está
          por publicar —la acción de la sección—, abajo cómo se vende
          (ADR-2026-10-06-metricas-y-lanzamientos-en-una-pagina, orden invertido a pedido de Maxi). */}
      {metricasVisibles && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-brand-black">Tus lanzamientos</h2>
          <p className="mt-0.5 text-sm text-neutral-500">
            Juntá productos con sus fotos, completá cada ficha y publicalos todos juntos.
          </p>
        </div>
      )}

      {cargando ? (
        <EsqueletoDeLanzamientos />
      ) : lanzamientos.length === 0 ? (
        <div className="mt-8 overflow-hidden rounded-3xl border border-[#1644EB]/10 bg-gradient-to-br from-[#F3F6FF] via-white to-[#EFFCFD] p-6 md:p-10">
          <p className="text-xs font-medium uppercase tracking-wide text-[#1644EB]">Cómo funciona</p>
          <p className="mt-2 max-w-lg text-lg font-semibold text-brand-black md:text-xl">
            De tus creaciones a tu tienda, en tres pasos
          </p>
          <ComoFunciona />
          <p className="mt-8 text-sm text-neutral-500">
            Empezá con el botón <span className="font-medium text-brand-black">Nuevo</span> de arriba. También
            podés tocar “Publicar en tu tienda online” en cualquier foto de Creaciones.
          </p>
        </div>
      ) : (
        <>
          {/* Con lanzamientos ya creados, los pasos van en una tira: recuerdan el camino
              sin empujar la lista para abajo. */}
          <ComoFunciona compacto />
          <div className="mt-8 space-y-10">
            <Grupo titulo="En preparación" items={preparando} onAbrir={(id) => navigate(`/lanzamientos/${id}`)} />
            <Grupo titulo="Publicados" items={publicados} onAbrir={(id) => navigate(`/lanzamientos/${id}`)} />
          </div>
        </>
      )}

      {metricasVisibles && (
        <div className="mt-12 border-t border-neutral-200 pt-4">
          <ResumenDeMetricas forzarSinTiendas={ensayo} />
        </div>
      )}
    </div>
  );
}

/**
 * Mientras cargan los lanzamientos: la tira de pasos y tres cards con la misma forma que
 * las reales (portada, nombre, renglón y pie). Ocupan el lugar desde el primer cuadro, así
 * que al llegar los datos la página no salta, y se lee qué está por aparecer mejor que un
 * "Cargando…" en el medio de la nada.
 */
function EsqueletoDeLanzamientos() {
  return (
    <div aria-busy="true" aria-label="Cargando tus lanzamientos">
      <div className="mt-6 h-[50px] animate-pulse rounded-2xl border border-[#1644EB]/10 bg-[#F3F6FF]" />
      <div className="mt-8 h-4 w-32 animate-pulse rounded-full bg-neutral-200" />
      <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card shadow-sm"
          >
            <div className="h-36 animate-pulse bg-neutral-100" />
            <div className="space-y-2.5 p-4">
              <div className="h-4 w-2/3 animate-pulse rounded-full bg-neutral-200" />
              <div className="h-3 w-1/2 animate-pulse rounded-full bg-neutral-100" />
              <div className="pt-3">
                <div className="h-3 w-28 animate-pulse rounded-full bg-[#EEF2FF]" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PASOS = [
  {
    icono: ImagePlus,
    titulo: 'Sumá productos',
    texto: 'Elegí fotos de tus creaciones: cada una entra con su producto.',
    color: 'bg-[#1644EB]/10 text-[#1644EB]',
  },
  {
    icono: ClipboardList,
    titulo: 'Completá la ficha',
    texto: 'Precio, stock, variantes y categoría. La IA te ayuda a llenarla.',
    color: 'bg-cyan-100 text-cyan-700',
  },
  {
    icono: Store,
    titulo: 'Publicá en tu tienda',
    texto: 'Tienda Nube, Mercado Libre, o un ZIP para cualquier otra plataforma.',
    color: 'bg-emerald-100 text-emerald-700',
  },
] as const;

/** Los tres pasos del módulo. Grande en la pantalla vacía; en una tira con la lista. */
function ComoFunciona({ compacto = false }: { compacto?: boolean }) {
  if (compacto) {
    return (
      <ol className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-[#1644EB]/10 bg-gradient-to-r from-[#F3F6FF] to-white py-3 px-4 text-sm">
        {PASOS.map((p, i) => (
          <li key={p.titulo} className="flex items-center gap-3">
            <span className="flex items-center gap-2">
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${p.color}`}>
                {i + 1}
              </span>
              <span className="font-medium text-brand-black">{p.titulo}</span>
            </span>
            {i < PASOS.length - 1 && <ArrowRight className="hidden h-3.5 w-3.5 text-neutral-300 sm:block" />}
          </li>
        ))}
      </ol>
    );
  }
  return (
    <ol className="mt-6 grid gap-3 md:grid-cols-3 md:gap-4">
      {PASOS.map((p, i) => (
        <li
          key={p.titulo}
          className="flex gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm backdrop-blur md:block md:p-5"
        >
          {/* En el teléfono, ícono al costado: tres cards altas empujaban el "Empezá" fuera de la pantalla. */}
          <span className="flex shrink-0 items-center justify-between">
            <span className={`grid h-10 w-10 place-items-center rounded-xl ${p.color}`}>
              <p.icono className="h-5 w-5" />
            </span>
            <span className="hidden text-xs font-semibold text-neutral-300 md:inline">0{i + 1}</span>
          </span>
          <span className="block min-w-0">
            <span className="block text-sm font-semibold text-brand-black md:mt-4">
              <span className="text-neutral-400 md:hidden">{i + 1}. </span>
              {p.titulo}
            </span>
            <span className="mt-1 block text-sm leading-snug text-neutral-500">{p.texto}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Grupo({
  titulo,
  items,
  onAbrir,
}: {
  titulo: string;
  items: Lanzamiento[];
  onAbrir: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <h2 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
        {titulo}
        <span className="rounded-full bg-neutral-100 py-0.5 px-2 text-[11px] tabular-nums text-neutral-600">
          {items.length}
        </span>
      </h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((l) => (
          <CardDeLanzamiento key={l.id} lanzamiento={l} onAbrir={() => onAbrir(l.id)} />
        ))}
      </div>
    </section>
  );
}

/**
 * Un lanzamiento: sus fotos arriba (lo que más rápido se reconoce), el estado y la
 * tienda encima de la foto, y abajo el nombre, qué tiene y cuánto se publicó.
 */
function CardDeLanzamiento({ lanzamiento: l, onAbrir }: { lanzamiento: Lanzamiento; onAbrir: () => void }) {
  const e = ESTADOS[l.estado];
  const avance = l.productos === 0 ? 0 : Math.round((l.publicados / l.productos) * 100);
  const yaSalio = l.estado !== 'borrador' || l.publicados > 0;
  return (
    <button
      type="button"
      onClick={onAbrir}
      className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card text-left shadow-sm transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-md"
    >
      <div className="relative">
        <Portada portadas={l.portadas} />
        <span
          className={`absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full py-1 px-2.5 text-[11px] font-medium shadow-sm ${e.clase}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${e.punto}`} aria-hidden="true" />
          {e.texto}
        </span>
        <span className="absolute right-3 top-3 flex -space-x-1.5" title={nombresDeDestinos(l.destinos)}>
          {l.destinos.map((d) => (
            <span key={d} className="rounded-[10px] shadow-sm ring-2 ring-white">
              <IconoDeCanal canal={d} />
            </span>
          ))}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <p className="line-clamp-2 text-base font-medium leading-snug text-brand-black">{l.nombre}</p>
        <p className="mt-1 text-xs text-neutral-500">
          {l.productos} producto{l.productos === 1 ? '' : 's'} · {l.imagenes} foto{l.imagenes === 1 ? '' : 's'} ·{' '}
          {nombresDeDestinos(l.destinos)}
        </p>

        {/* El pie queda apoyado abajo: con `mt-auto` todas las cards de la fila
            cierran a la misma altura aunque el nombre ocupe uno o dos renglones. */}
        <div className="mt-auto pt-4">
          {yaSalio && l.productos > 0 ? (
            <div>
              <div className="flex items-center justify-between text-[11px] text-neutral-500">
                <span>
                  {l.publicados} de {l.productos} publicados
                  {l.conError > 0 && <span className="text-red-600"> · {l.conError} con error</span>}
                </span>
                <span className="tabular-nums">{avance}%</span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-neutral-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#1644EB] to-[#60E7F0]"
                  style={{ width: `${avance}%` }}
                />
              </div>
            </div>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#1644EB]">
              {l.productos === 0 ? 'Sumale productos' : 'Seguí armándolo'}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          )}
          {l.fechaLanzamiento && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-neutral-400">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(`${l.fechaLanzamiento}T00:00:00`).toLocaleDateString('es-AR', {
                day: 'numeric',
                month: 'long',
              })}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

/** Las primeras fotos del lanzamiento, en mosaico. Sin fotos, un fondo con la marca. */
function Portada({ portadas }: { portadas: string[] }) {
  const alto = 'h-36';
  if (portadas.length === 0) {
    return (
      <div
        className={`${alto} grid place-items-center bg-gradient-to-br from-[#E8EEFF] via-[#F3F6FF] to-[#E3FAFC] text-[#1644EB]/40`}
      >
        <Images className="h-8 w-8" />
      </div>
    );
  }
  const [a, b, c] = portadas;
  const img = (src: string) => (
    <CachedImg src={src} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
  );
  if (portadas.length === 1) return <div className={`${alto} overflow-hidden bg-neutral-100`}>{img(a!)}</div>;
  if (portadas.length === 2) {
    return (
      <div className={`${alto} grid grid-cols-2 gap-0.5 overflow-hidden bg-neutral-100`}>
        <div className="overflow-hidden">{img(a!)}</div>
        <div className="overflow-hidden">{img(b!)}</div>
      </div>
    );
  }
  return (
    <div className={`${alto} grid grid-cols-3 grid-rows-2 gap-0.5 overflow-hidden bg-neutral-100`}>
      <div className="col-span-2 row-span-2 overflow-hidden">{img(a!)}</div>
      <div className="overflow-hidden">{img(b!)}</div>
      <div className="overflow-hidden">{img(c!)}</div>
    </div>
  );
}

function NuevoLanzamiento({
  companyId,
  brandId,
  creando,
  setCreando,
  abiertoAlInicio = false,
  onCreado,
}: {
  companyId: string | null;
  brandId: string | null;
  creando: boolean;
  setCreando: (v: boolean) => void;
  abiertoAlInicio?: boolean;
  onCreado: (l: Lanzamiento) => void;
}) {
  const [abierto, setAbierto] = useState(abiertoAlInicio);
  const [nombre, setNombre] = useState('');
  /**
   * Los destinos elegidos. Las tiendas que crean productos solas (Tienda Nube y Mercado Libre) se
   * pueden elegir juntas; el resto va solo (ADR-2026-09-30-lanzamiento-a-varias-tiendas).
   */
  const [elegidos, setElegidos] = useState<CanalLanzamiento[]>(['tiendanube']);
  // Se consulta con la página, no al abrir: si no, Mercado Libre aparecía un segundo después en el
  // modal (prueba del 2/10). Es una consulta por visita.
  const ml = useMercadoLibreDisponible(brandId);
  const elegir = (id: CanalLanzamiento) =>
    setElegidos((antes) => {
      if (!creaProductos(id) || !antes.every(creaProductos)) return [id];
      if (!antes.includes(id)) return [...antes, id];
      // Siempre queda al menos una.
      return antes.length > 1 ? antes.filter((x) => x !== id) : antes;
    });
  /** Lo que falta, recién después de intentar crear. Ver el comentario del botón. */
  const [falta, setFalta] = useState<string | null>(null);
  const campoNombre = useRef<HTMLInputElement>(null);
  // El cartel del destino, la primera vez que se abre el modal. Es lo único que el
  // modal no dice: que el destino no se cambia después.
  useRecorrido('tienda_tour_destino', abierto, pasosDelDestino, { dentroDeUnModal: true });

  const crear = async () => {
    // Enter no pasa por el botón (que se apaga mientras crea): sin esto, dos Enter seguidos creaban
    // dos lanzamientos (review #168).
    if (creando) return;
    if (!nombre.trim()) {
      // El botón ya NO está deshabilitado: ver el comentario de abajo.
      setFalta('Escribí un nombre para el lanzamiento.');
      campoNombre.current?.focus();
      return;
    }
    if (!companyId) return;
    setCreando(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const l = await crearLanzamiento({
      companyId,
      brandId,
      nombre,
      canal: elegidos[0]!,
      destinos: elegidos,
      userId: user?.id ?? null,
    });
    setCreando(false);
    if (l) onCreado(l);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          // Si el cartel de "Empezá por acá" está señalando este botón, ya cumplió:
          // se cierra para no quedar encima del modal.
          cerrarRecorridoEnCurso();
          setAbierto(true);
        }}
        data-tour="tienda-nuevo"
        className={boton('primario', { extra: 'shrink-0' })}
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">Nuevo lanzamiento</span>
        <span className="sm:hidden">Nuevo</span>
      </button>

      {abierto && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 backdrop-blur-[2px] sm:items-center sm:p-4">
          {/* Con los destinos agrupados el modal puede pasar el alto de un
              teléfono chico: el medio scrollea y el pie con "Crear" queda fijo
              abajo, en vez de quedar debajo del borde de la pantalla. */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-nuevo-lanzamiento"
            className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
          >
            <div className="flex items-start gap-3 border-b border-neutral-100 bg-gradient-to-br from-[#F3F6FF] to-white p-6 pb-5">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#EEF2FF] text-[#1644EB]">
                <Rocket className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 id="titulo-nuevo-lanzamiento" className="text-lg font-semibold text-brand-black">
                  Nuevo lanzamiento
                </h2>
                <p className="mt-0.5 text-sm text-neutral-500">
                  Ponele un nombre y elegí a dónde va. Los productos y las fotos los sumás después.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAbierto(false)}
                aria-label="Cerrar"
                className="-mr-2 -mt-2 grid h-9 w-9 shrink-0 place-items-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-brand-black"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <label htmlFor="nombre-del-lanzamiento" className="flex items-center gap-2 text-sm font-medium text-brand-black">
                <NumeroDeCampo n={1} /> Nombre
              </label>
              <input
                id="nombre-del-lanzamiento"
                ref={campoNombre}
                autoFocus
                value={nombre}
                onChange={(e) => {
                  setNombre(e.target.value);
                  // Se limpia al escribir: dejar el error rojo mientras la persona
                  // está justo arreglándolo la reta por algo que ya resolvió.
                  if (falta) setFalta(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && void crear()}
                aria-invalid={falta ? true : undefined}
                aria-describedby={falta ? 'falta-el-nombre' : 'ayuda-del-nombre'}
                placeholder="Colección primavera"
                className={`mt-2 w-full rounded-xl border bg-white py-3 px-4 text-sm text-brand-black outline-none transition-shadow placeholder:text-neutral-400 ${
                  falta
                    ? 'border-red-400 focus:ring-4 focus:ring-red-100'
                    : 'border-neutral-200 focus:border-[#1644EB] focus:ring-4 focus:ring-[#1644EB]/10'
                }`}
              />
              {falta ? (
                <p id="falta-el-nombre" role="alert" className="mt-1.5 text-xs text-red-600">
                  {falta}
                </p>
              ) : (
                <p id="ayuda-del-nombre" className="mt-1.5 text-xs text-neutral-400">
                  Para reconocerlo en la lista. Por ejemplo: Hot Sale, Invierno 2026.
                </p>
              )}

              <p className="mt-6 flex items-center gap-2 text-sm font-medium text-brand-black">
                <NumeroDeCampo n={2} /> ¿A dónde va?
              </p>
              {/* Agrupados por a qué se publica: una tienda online y una red
                  social no compiten entre sí, y en una lista plana había que leer
                  las cuatro para encontrar la que se busca. */}
              <div data-tour="tienda-destino" className="mt-3 space-y-4">
                {canalesPorGrupo(canalesParaElegir(ml.disponible)).map((g) => (
                  <div key={g.id}>
                    <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-400">{g.titulo}</p>
                    {g.id === 'tienda' && ml.disponible && (
                      <p className="mb-2 rounded-lg bg-[#F5F7FF] py-2 px-3 text-xs text-[#1644EB]">
                        Tienda Nube y Mercado Libre se pueden elegir juntas: se publica en las dos.
                      </p>
                    )}
                    <div className="space-y-2">
                      {g.canales.map((c) => {
                        const puesto = elegidos.includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            disabled={!c.sePuedeArmar}
                            onClick={() => elegir(c.id)}
                            aria-pressed={puesto}
                            className={`flex w-full items-center gap-3 rounded-2xl border py-3 px-4 text-left transition-[border-color,background-color,box-shadow] ${
                              !c.sePuedeArmar
                                ? 'cursor-not-allowed border-dashed border-neutral-200 bg-neutral-50/60 opacity-60'
                                : puesto
                                  ? 'border-[#1644EB] bg-[#F5F7FF] shadow-[0_0_0_3px_rgba(22,68,235,0.08)]'
                                  : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
                            }`}
                          >
                            <IconoDeCanal canal={c.id} />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2 text-sm font-medium text-brand-black">
                                {c.nombre}
                              </span>
                              <span className="block text-xs text-neutral-500">{c.nota}</span>
                            </span>
                            {c.sePuedeArmar ? (
                              <span
                                aria-hidden="true"
                                className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition-colors ${
                                  puesto ? 'border-[#1644EB] bg-[#1644EB] text-white' : 'border-neutral-300 bg-white'
                                }`}
                              >
                                {puesto && <Check className="h-3 w-3" strokeWidth={3} />}
                              </span>
                            ) : (
                              <span className="shrink-0 rounded-full bg-neutral-100 py-0.5 px-2 text-[11px] font-medium text-neutral-500">
                                Próximamente
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 border-t border-neutral-100 bg-white p-4 sm:px-6">
              <button type="button" onClick={() => setAbierto(false)} className={boton('neutro', { extra: 'flex-1' })}>
                Cancelar
              </button>
              {/* Sin nombre el botón se APRIETA y contesta, en vez de quedarse
                  apagado. Un botón deshabilitado no explica qué le falta: quien
                  no ve el campo vacío toca, no pasa nada, y no hay a dónde
                  mirar (ADR-2026-09-15-el-nombre-del-lanzamiento-se-pide-al-crear). */}
              <button
                type="button"
                onClick={() => void crear()}
                disabled={creando}
                className={boton('primario', { extra: 'flex-[2]' })}
              >
                {creando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {creando ? 'Creando…' : 'Crear lanzamiento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** El número de cada campo del modal: dice que son dos cosas y en qué orden. */
function NumeroDeCampo({ n }: { n: number }) {
  return (
    <span className="grid h-5 w-5 place-items-center rounded-full bg-[#EEF2FF] text-[11px] font-semibold text-[#1644EB]">
      {n}
    </span>
  );
}
